import React, { useState } from "react";
import { ProjectLog } from "./AdminDashboard";
import { FaDatabase, FaCode, FaServer, FaGithub, FaExternalLinkAlt, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

interface ProjectContainerProps {
  projects: ProjectLog[];
}

export const ProjectContainer: React.FC<ProjectContainerProps> = ({ projects }) => {
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [selectedProject, setSelectedProject] = useState<ProjectLog | null>(null);

  const categories = ["ALL", "FULL STACK", "FRONTEND", "BACKEND", "DEVOPS"];

  // Filter projects by category
  const filteredProjects = projects.filter((p) => {
    if (activeFilter === "ALL") return true;
    const cat = (p.category || "").toUpperCase();
    if (activeFilter === "BACKEND") {
      return cat === "BACKEND" || cat === "FULL STACK";
    }
    if (activeFilter === "FRONTEND") {
      return cat === "FRONTEND" || cat === "FULL STACK";
    }
    return cat === activeFilter;
  });

  const getBadgeStyle = (badge: string) => {
    const b = badge.toLowerCase();
    if (b.includes("react") || b.includes("typescript") || b.includes("tailwind")) {
      return "border-cyber-cyan text-cyber-cyan shadow-glow-cyan-sm";
    }
    if (b.includes("postgres") || b.includes("mongo") || b.includes("db") || b.includes("sql")) {
      return "border-emerald-400 text-emerald-400";
    }
    return "border-cyber-purple text-cyber-purple shadow-glow-purple-sm";
  };

  const getIcon = (badges: string[], category?: string) => {
    const cat = (category || "").toLowerCase();
    if (cat === "devops" || badges.some(b => b.toLowerCase().includes("pm2") || b.toLowerCase().includes("terminus"))) {
      return <FaServer className="w-4 h-4" />;
    }
    if (cat === "backend" || badges.some(b => b.toLowerCase().includes("db") || b.toLowerCase().includes("mongo") || b.toLowerCase().includes("postgres"))) {
      return <FaDatabase className="w-4 h-4" />;
    }
    return <FaCode className="w-4 h-4" />;
  };

  return (
    <div
      id="project"
      className="w-full min-h-screen bg-cyber-black/20 text-gray-300 py-24 px-6 flex items-center justify-center border-t border-cyber-cyan/10 relative z-10"
    >
      <div className="max-w-5xl w-full space-y-12">
        
        {/* Section Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-cyber-cyan font-mono text-xs tracking-[0.25em] uppercase mb-2"
            >
              // PORTFOLIO_ARCHIVE
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-3xl font-mono font-bold text-white inline-block border-b-2 border-cyber-cyan pb-2"
            >
              FEATURED_MODULES
            </motion.h2>
          </div>

          {/* CMS Sync Indicator */}
          <div className="font-mono text-[10px] text-cyber-purple bg-cyber-purple/10 border border-cyber-purple/30 px-3 py-1.5 rounded-lg shadow-glow-purple-sm">
            STATUS: ACTIVE // CMS_SYNC_ONLINE
          </div>
        </div>

        {/* Categories Filter Tabs */}
        <div className="flex flex-wrap gap-2 py-2 border-b border-white/5">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`px-4 py-2 font-mono text-xs tracking-wider border rounded-lg transition-all duration-300 ${
                activeFilter === cat
                  ? "bg-cyber-cyan text-black border-cyber-cyan font-bold shadow-glow-cyan-sm"
                  : "bg-transparent border-cyber-gray-600/40 text-cyber-gray-300 hover:border-cyber-cyan hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          id="projects"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((proj, index) => (
              <motion.div
                layout
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                key={proj.id}
                onClick={() => setSelectedProject(proj)}
                className={`project-card border bg-cyber-darker/60 hover:bg-cyber-darker/80 hover:shadow-glow-cyan-sm p-6 rounded-2xl transition-all duration-300 flex flex-col justify-between group relative overflow-hidden cursor-pointer ${
                  proj.featured ? "border-cyber-purple/40" : "border-cyber-gray-600/20"
                }`}
              >
                {/* Visual Accent Glows */}
                <div className="absolute top-0 right-0 w-12 h-12 bg-cyber-cyan/5 rounded-full filter blur-[15px] opacity-0 group-hover:opacity-100 transition duration-500" />

                <div>
                  {/* Card Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-2">
                      <div className={`p-2 rounded border ${
                        proj.featured
                          ? "bg-cyber-purple/10 text-cyber-purple border-cyber-purple/20"
                          : "bg-cyber-cyan/10 text-cyber-cyan border-cyber-cyan/20"
                      }`}>
                        {getIcon(proj.badges, proj.category)}
                      </div>
                      <span className="text-[9px] font-mono text-cyber-gray-400 tracking-widest uppercase">
                        {proj.category || "Full Stack"} LOG // {proj.id.slice(0, 6)}
                      </span>
                    </div>
                    
                    <span className="text-[10px] font-mono text-cyber-gray-500 bg-cyber-black px-2 py-0.5 border border-cyber-gray-800 rounded">
                      {proj.date}
                    </span>
                  </div>

                  {/* Title & Description */}
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="project-title text-base font-mono font-bold text-white group-hover:text-cyber-cyan transition duration-200">
                      {proj.name}
                    </h3>
                    {proj.featured && (
                      <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 bg-cyber-purple/10 border border-cyber-purple/40 text-cyber-purple rounded uppercase">
                        FEATURED
                      </span>
                    )}
                  </div>
                  
                  <p className="project-description text-xs text-cyber-gray-300 font-sans leading-relaxed mb-6 line-clamp-3">
                    {proj.description}
                  </p>
                </div>

                {/* Footer specs & Actions */}
                <div className="space-y-4">
                  <div>
                    <div className="text-[9px] font-mono text-cyber-gray-500 mb-1.5 uppercase">
                      TECH_STACK:
                    </div>
                    <div className="project-tech-stack flex flex-wrap gap-1.5">
                      {proj.badges.map((badge, idx) => (
                        <span
                          key={idx}
                          className={`text-[9px] font-mono px-2 py-0.5 bg-cyber-black border rounded ${getBadgeStyle(badge)}`}
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* View Details Indicator */}
                  <div className="text-[10px] font-mono text-cyber-cyan flex items-center justify-end space-x-1 opacity-70 group-hover:opacity-100 transition-all duration-300">
                    <span>LAUNCH_DETAILS_MOD()</span>
                    <FaExternalLinkAlt size={8} />
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {filteredProjects.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-2 text-center py-24 border border-dashed border-cyber-gray-600/20 rounded-2xl"
            >
              <p className="font-mono text-xs text-cyber-gray-500">
                NO ACTIVE LOGS FOUND IN THIS CATEGORY.
              </p>
            </motion.div>
          )}
        </motion.div>

      </div>

      {/* Project Details Modal Popup */}
      <AnimatePresence>
        {selectedProject && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="max-w-2xl w-full bg-cyber-darker border border-cyber-cyan/35 rounded-2xl shadow-glow-cyan overflow-hidden flex flex-col font-mono text-xs text-gray-300"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center bg-cyber-dark px-6 py-4 border-b border-cyber-cyan/15">
                <div className="flex items-center space-x-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-cyber-cyan animate-pulse" />
                  <span className="font-bold tracking-widest text-cyber-cyan">
                    PROJECT_SPECS // {selectedProject.name.toUpperCase().replace(/\s+/g, '_')}
                  </span>
                </div>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="text-cyber-gray-400 hover:text-red-500 transition duration-200 flex items-center space-x-1"
                >
                  <FaTimes size={14} />
                  <span>[CLOSE]</span>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
                {/* Project Image Preview */}
                {selectedProject.imageUrl && (
                  <div className="relative border border-cyber-gray-800 rounded-xl overflow-hidden bg-black/50 aspect-video">
                    <img
                      src={selectedProject.imageUrl}
                      alt={selectedProject.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_95%,rgba(0,240,255,0.05)_95%)] bg-[size:100%_12px] pointer-events-none opacity-40" />
                  </div>
                )}

                {/* Details Section */}
                <div className="space-y-4">
                  <div className="flex flex-wrap justify-between items-center border-b border-cyber-gray-800 pb-3 gap-2">
                    <div>
                      <span className="text-cyber-gray-500">CATEGORY:</span>
                      <span className="text-white ml-2 font-bold">{selectedProject.category || "Full Stack"}</span>
                    </div>
                    <div>
                      <span className="text-cyber-gray-500">TIMELINE:</span>
                      <span className="text-cyber-purple ml-2 font-bold">{selectedProject.date}</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <span className="text-cyber-cyan block">// DESCRIPTION:</span>
                    <p className="text-white font-sans text-sm leading-relaxed whitespace-pre-line bg-cyber-black/40 border border-cyber-gray-800/30 p-4 rounded-xl">
                      {selectedProject.description}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <span className="text-cyber-cyan block">// INTEGRATED_TECH_STACK:</span>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {selectedProject.badges.map((badge, idx) => (
                        <span
                          key={idx}
                          className={`text-[10px] px-2.5 py-1 bg-cyber-black border rounded-lg ${getBadgeStyle(badge)}`}
                        >
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modal Footer (Action Links) */}
              <div className="px-6 py-4 bg-cyber-dark border-t border-cyber-cyan/15 flex justify-end items-center gap-4">
                {selectedProject.githubUrl && (
                  <a
                    href={selectedProject.githubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 border border-cyber-gray-600 hover:border-white hover:text-white transition duration-300 rounded font-bold"
                  >
                    <FaGithub size={14} />
                    <span>SOURCE_CODE</span>
                  </a>
                )}
                {selectedProject.liveUrl && (
                  <a
                    href={selectedProject.liveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-cyber-cyan text-black hover:bg-white hover:shadow-glow-cyan-sm transition duration-300 rounded font-bold"
                  >
                    <FaExternalLinkAlt size={12} />
                    <span>LIVE_MODULE</span>
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectContainer;
